// ============================================================================
// conquest.js — the strategic layer that sits ABOVE the tactical battles.
// The realm is a graph of regions across seven biomes. Benjen's Ashen Host holds
// a central homeland; three enemy factions (Magmar south / Frost north / Carrion
// east) press on his borders. Each "day":
//   • dispatched generals march / resolve / rest,
//   • standing invasions tick down (unanswered ones hit the garrison),
//   • each enemy faction builds HEAT and, when hot enough, launches invasions —
//     the tempo escalates with the day count, how many fronts it holds, whether
//     it's cornered (desperation), and a GRUDGE it nurses when you take its land,
//     so a cornered or wronged faction throws multiple fronts at once late-war,
//   • a weighted EVENT DECK fires the colour of a living realm: betrayals,
//     revolts, plagues, omens, sellswords, enemy feuds, spies, uprisings.
// The player answers threats by MARCHING (a tactical battle) or DISPATCHING a
// general (auto-resolved). Lose Redmarch and the war is lost; take a faction's
// seat and it collapses. Pure module — no Three.js/DOM — so it Monte-Carlos
// headlessly (scratchpad/conquest_simtest.mjs). All randomness flows through an
// injectable rng (defaults to Math.random) so tests are reproducible.
// ============================================================================

import { GENERAL_ROSTER } from './generals.js';
import { WARLORD_ROSTER } from '../ai/warlords.js';

// ---- factions -------------------------------------------------------------
export const FACTIONS = {
  ashen:   { key: 'ashen',   name: 'The Ashen Host',      color: '#9fb4cc', crest: 'art/crest_ashen.png',   crowdTint: 0xb7c0cf, crowdEmber: 0x3a5c8a },
  magmar:  { key: 'magmar',  name: 'The Magmar Dynasty',  color: '#ff6a1a', crest: 'art/crest_magmar.png',  crowdTint: 0xcf6038, crowdEmber: 0xff5a12 },
  frost:   { key: 'frost',   name: 'The Frost Raiders',   color: '#8fd0ff', crest: 'art/crest_frost.png',   crowdTint: 0x9fc2e0, crowdEmber: 0x6fb0e6 },
  carrion: { key: 'carrion', name: 'The Carrion',         color: '#a6c05a', crest: 'art/crest_carrion.png', crowdTint: 0x93a860, crowdEmber: 0x8fbf40 },
};
export const ENEMY_FACTIONS = ['magmar', 'frost', 'carrion'];

// named invasion commanders per enemy faction (Frost/Carrion appended to the roster)
const EXTRA_COMMANDERS = [
  { name: 'Jarl Hoarfrost',  title: 'the Winter That Walks', hp: 1150, reach: 3.9, guardBreakPhase: 3, x: -34, z: -78, weapon: 'maul',   faction: 'frost' },
  { name: 'Yrsa Palewind',   title: 'the Rime-Spear',        hp: 980,  reach: 4.0, guardBreakPhase: 2, x:  34, z: -78, weapon: 'spear',  faction: 'frost' },
  { name: 'Gravelord Murk',  title: 'the Rot-Crowned',       hp: 1200, reach: 4.0, guardBreakPhase: 3, x: -34, z: -78, weapon: 'scythe', faction: 'carrion' },
  { name: 'Sythe the Hollow', title: 'the Bog-Butcher',      hp: 1020, reach: 3.3, guardBreakPhase: 2, x:  34, z: -78, weapon: 'twin',   faction: 'carrion' },
];
for (const c of EXTRA_COMMANDERS) if (!WARLORD_ROSTER.some((r) => r.name === c.name)) WARLORD_ROSTER.push(c);

const COMMANDERS = {
  magmar:  ['Vurmog', 'Tholvar', 'Draxa Embermaw', 'Ashlord Kaevun', 'Skarn Duraghast', 'Ysra Coalheart'],
  frost:   ['Jarl Hoarfrost', 'Yrsa Palewind'],
  carrion: ['Gravelord Murk', 'Sythe the Hollow'],
};

// ---- the realm ------------------------------------------------------------
const REGION_DEFS = [
  { id: 'redmarch',  name: 'Redmarch',    biome: 'ashlands',  x: 50, y: 54, owner: 'ashen',  seat: true,  garrison: 150, desc: 'The Ashen Host\'s seat, ringed by ash-flats. If Redmarch falls, the war is lost.' },
  { id: 'greyhold',  name: 'Greyhold',    biome: 'ironpeak',  x: 44, y: 33, owner: 'ashen',  garrison: 110, desc: 'A grey highland hold guarding the northern passes against the Frost.' },
  { id: 'thornfen',  name: 'Thornfen',    biome: 'duskmarsh', x: 67, y: 49, owner: 'ashen',  garrison: 105, desc: 'A misted march on the eastern border, first to feel the Carrion\'s creep.' },
  { id: 'goldfen',   name: 'Goldfen Vale', biome: 'goldfen',  x: 28, y: 60, owner: 'ashen',  garrison: 95,  desc: 'The Host\'s breadbasket — golden fields that feed the war.' },
  { id: 'saltmere',  name: 'Saltmere',    biome: 'saltmere',  x: 32, y: 79, owner: 'ashen',  garrison: 90,  desc: 'A rocky sea-coast to the southwest, open to raids from the ember south.' },
  { id: 'emberwood', name: 'Emberwood',   biome: 'emberwood', x: 56, y: 73, owner: 'magmar', garrison: 130, desc: 'A burnt pine forest smouldering with Magmar warbands.' },
  { id: 'cinderhold', name: 'Cinderhold', biome: 'ashlands',  x: 50, y: 91, owner: 'magmar', seat: true,  garrison: 190, desc: 'The Magmar seat, a fortress of black slag. Take it and the Dynasty breaks.' },
  { id: 'ashpit',    name: 'Ashpit',      biome: 'emberwood', x: 70, y: 84, owner: 'magmar', garrison: 120, desc: 'Slag-quarries worked by Magmar thralls in the smoking south-east.' },
  { id: 'palefrost', name: 'Palefrost',   biome: 'frostmark', x: 60, y: 20, owner: 'frost',  garrison: 115, desc: 'A frozen foothold from which the Frost Raiders descend on the passes.' },
  { id: 'frostmark', name: 'Frostmark',   biome: 'frostmark', x: 46, y: 10, owner: 'frost',  seat: true,  garrison: 175, desc: 'The Frost seat, a glacier-hall in the far north.' },
  { id: 'ravenmoor', name: 'Ravenmoor',   biome: 'duskmarsh', x: 82, y: 64, owner: 'carrion', garrison: 115, desc: 'Rotting moorland where the Carrion muster their plague-host.' },
  { id: 'duskmoor',  name: 'Duskmoor',    biome: 'duskmarsh', x: 84, y: 42, owner: 'carrion', seat: true,  garrison: 175, desc: 'The Carrion seat, a drowned keep sunk in the eastern bog.' },
];
const NEIGHBORS = {
  redmarch: ['greyhold', 'thornfen', 'goldfen', 'emberwood'],
  greyhold: ['redmarch', 'goldfen', 'palefrost', 'frostmark'],
  thornfen: ['redmarch', 'emberwood', 'ravenmoor', 'duskmoor', 'palefrost'],
  goldfen: ['redmarch', 'greyhold', 'saltmere'],
  saltmere: ['goldfen', 'emberwood'],
  emberwood: ['redmarch', 'thornfen', 'saltmere', 'cinderhold', 'ashpit'],
  cinderhold: ['emberwood', 'ashpit'],
  ashpit: ['emberwood', 'cinderhold', 'ravenmoor'],
  palefrost: ['greyhold', 'thornfen', 'frostmark'],
  frostmark: ['greyhold', 'palefrost'],
  ravenmoor: ['thornfen', 'ashpit', 'duskmoor'],
  duskmoor: ['thornfen', 'ravenmoor'],
};

export function generalPower(name) {
  const g = GENERAL_ROSTER[name];
  return g ? Math.round(g.hp / 13) : 100;
}

// ---- all pacing/tuning knobs in one place (sim-balance-lab: move numbers here) ----
const CFG = {
  DISPATCH_TRAVEL: 2, DISPATCH_COOLDOWN: 2, THREAT_DAYS: 4, GRACE_DAYS: 2,
  HEAT_RATE: 0.30,        // base heat gained per active faction per day
  HEAT_DAY: 0.006,        // aggression growth per day of war
  FRONT_AGGR: 0.12,       // extra aggression per frontier beyond the first
  DESPERATION: 0.55,      // extra aggression when only the faction's seat remains
  GRUDGE_HIT: 0.55,       // grudge gained when the player takes this faction's land
  GRUDGE_DECAY: 0.035,
  STR_BASE: 52, STR_DAY: 1.35, STR_AGGR: 24, STR_GRUDGE: 16,
  EVENT_START_AFTER_GRACE: 2,   // first day the deck can fire = GRACE + this
  EVENT_CHANCE: 0.5,            // per-day chance to draw (when off cooldown)
  EVENT_CD: [2, 4],
  MERC_DAYS: [6, 11],
  UNREST_RISE: 0.1,            // /day in a restless just-conquered region
  RECOVER: 4,                  // garrison a quiet player region regains/day
};
const MERC_NAMES = ['the Grey Wolves', 'the Iron Sellswords', 'the Ashen Kites', 'the Broken Spears', 'the Free Reavers', 'the Salt Dogs'];

export class Conquest {
  constructor(saved, rng) {
    this.rng = rng || Math.random;
    this.day = 1;
    this.over = null;
    this.log = [];
    this.regions = {};
    for (const d of REGION_DEFS) {
      this.regions[d.id] = { ...d, neighbors: NEIGHBORS[d.id], baseGarrison: d.garrison, threat: null, unrest: 0, capturedFrom: null };
    }
    this.factionDefeated = { magmar: false, frost: false, carrion: false };
    this.factionState = {};   // per enemy faction: { heat, grudge }
    for (const f of ENEMY_FACTIONS) this.factionState[f] = { heat: 0, grudge: 0 };
    this.generals = {};
    for (const name of Object.keys(GENERAL_ROSTER)) {
      this.generals[name] = { name, power: generalPower(name), status: 'ready', cd: 0, dest: null, mode: null, eta: 0, weapon: GENERAL_ROSTER[name].weapon };
    }
    this.eventCd = CFG.GRACE_DAYS + CFG.EVENT_START_AFTER_GRACE;
    this._mercN = 0;
    if (saved) this._load(saved);
    else this._log('The war for the realm begins. Hold your lands, Commander.');
  }

  // ---- rng helpers (all randomness routes through these) ----
  _rnd(a, b) { return a + this.rng() * (b - a); }
  _int(a, b) { return Math.floor(this._rnd(a, b + 1)); }
  _chance(p) { return this.rng() < p; }
  _pick(a) { return a.length ? a[Math.floor(this.rng() * a.length)] : null; }
  _shuffle(a) { for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(this.rng() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }

  // ---- queries ----
  region(id) { return this.regions[id]; }
  list() { return REGION_DEFS.map((d) => this.regions[d.id]); }
  faction(k) { return FACTIONS[k]; }
  ownerFaction(id) { return FACTIONS[this.regions[id].owner]; }
  isPlayer(id) { return this.regions[id].owner === 'ashen'; }
  edges() {
    const seen = new Set(), out = [];
    for (const d of REGION_DEFS) for (const n of NEIGHBORS[d.id]) {
      const k = d.id < n ? d.id + '|' + n : n + '|' + d.id;
      if (seen.has(k)) continue; seen.add(k);
      out.push([this.regions[d.id], this.regions[n]]);
    }
    return out;
  }
  readyGenerals() { return Object.values(this.generals).filter((g) => g.status === 'ready'); }
  _realReady() { return this.readyGenerals().filter((g) => !g.merc); }
  isFrontline(id) { const r = this.regions[id]; return r.owner === 'ashen' && r.neighbors.some((n) => this.regions[n].owner !== 'ashen'); }
  assaultable() { return this.list().filter((r) => r.owner !== 'ashen' && r.neighbors.some((n) => this.regions[n].owner === 'ashen')); }
  playerRegions() { return this.list().filter((r) => r.owner === 'ashen'); }

  actions(id) {
    const r = this.regions[id];
    const a = { march: false, dispatch: false, walk: false, assault: false };
    if (this.over) return a;
    if (r.owner === 'ashen') {
      a.walk = true;
      if (r.threat) { a.march = true; a.dispatch = this.readyGenerals().length > 0; }
    } else if (r.neighbors.some((n) => this.regions[n].owner === 'ashen')) {
      a.assault = true; a.march = true; a.dispatch = this.readyGenerals().length > 0;
    }
    return a;
  }

  // ---- the daily tide -------------------------------------------------------
  advanceDay() {
    if (this.over) return [];
    const events = [];
    this.day++;

    // 1) general armies march / rest / recover / desert-then-return; mercs ride off at term's end
    for (const g of Object.values(this.generals)) {
      if (g.status === 'resting' || g.status === 'wounded') {
        if (--g.cd <= 0) { const was = g.status; g.status = 'ready'; g.cd = 0; if (was === 'wounded') events.push(`${g.name} has mended his wounds and returns to the field.`); }
      } else if (g.status === 'marching') {
        if (--g.eta <= 0) { events.push(this.resolveDispatch(g)); g.status = 'resting'; g.cd = CFG.DISPATCH_COOLDOWN; g.dest = null; g.mode = null; }
      } else if (g.status === 'gone') {
        if (this._chance(0.09)) { g.status = 'ready'; g.cd = 0; events.push(`${g.name} returns to the banner — his pride cooled, his sword is Benjen's again.`); }
      }
      if (g.merc && g.status === 'ready' && this.day >= g.leaveDay) { delete this.generals[g.name]; this._log(`${g.name} have taken their pay and ridden on.`); }
    }

    // 2) standing invasions tick; unanswered ones resolve against the garrison
    for (const r of this.list()) {
      if (r.owner === 'ashen' && r.threat && --r.threat.days <= 0) {
        const t = r.threat;
        if (r.garrison >= t.strength) { r.threat = null; r.garrison = Math.max(40, Math.round(r.garrison - t.strength * 0.4)); events.push(`${r.name}'s garrison threw back ${FACTIONS[t.faction].name}.`); }
        else { events.push(`⚔ ${r.name} has FALLEN to ${FACTIONS[t.faction].name}!`); this._capture(r, t.faction); }
      }
    }

    // 3) enemy HEAT → invasions (escalates with day / fronts / desperation / grudge)
    if (this.day > CFG.GRACE_DAYS) {
      for (const f of ENEMY_FACTIONS) {
        if (this.factionDefeated[f]) continue;
        const fronts = this._frontiers(f);
        if (!fronts.length) continue;                 // no shared border → cannot invade
        const st = this.factionState[f];
        const aggr = 1 + this.day * CFG.HEAT_DAY + (fronts.length - 1) * CFG.FRONT_AGGR + st.grudge + (this._onlySeat(f) ? CFG.DESPERATION : 0);
        st.heat += CFG.HEAT_RATE * aggr;
        st.grudge = Math.max(0, st.grudge - CFG.GRUDGE_DECAY);
        let guard = 0;
        while (st.heat >= 1 && guard++ < 3) {          // spend heat → up to 3 fronts a day when boiling
          st.heat -= 1;
          const strength = Math.round(CFG.STR_BASE + this.day * CFG.STR_DAY + (aggr - 1) * CFG.STR_AGGR + st.grudge * CFG.STR_GRUDGE + this._rnd(-8, 12));
          const line = this._launchInvasion(f, strength);
          if (line) events.push(line); else { st.heat += 0.5; break; }   // nowhere new to strike this pass
        }
      }
    }

    // 4) events — the colour of a living realm
    if (--this.eventCd <= 0) {
      if (this._chance(CFG.EVENT_CHANCE)) { const ev = this._drawEvent(); if (ev) { const out = ev.fire(this); (Array.isArray(out) ? out : [out]).forEach((l) => l && events.push(l)); this.eventCd = this._int(CFG.EVENT_CD[0], CFG.EVENT_CD[1]); } else this.eventCd = 1; }
      else this.eventCd = this._int(CFG.EVENT_CD[0], CFG.EVENT_CD[1]);
    }

    // 5) unrest + garrison recovery. Freshly conquered enemy land is RESTLESS:
    // it climbs toward revolt and recovers its garrison slowly, so a sprawling
    // conqueror who doesn't consolidate gets uprisings behind his lines. Only a
    // near-full garrison pacifies it (and then it's finally "yours").
    for (const r of this.list()) {
      if (r.owner !== 'ashen') continue;
      if (r.capturedFrom) {
        if (r.garrison >= r.baseGarrison * 0.9) { r.unrest = Math.max(0, r.unrest - 0.06); if (r.unrest <= 0) r.capturedFrom = null; }
        else r.unrest = Math.min(1, r.unrest + CFG.UNREST_RISE * (r.threat ? 0.4 : 1));
      }
      if (!r.threat && r.garrison < r.baseGarrison) r.garrison += r.capturedFrom ? Math.round(CFG.RECOVER * 0.4) : CFG.RECOVER;
    }

    events.forEach((e) => this._log(e));
    this._checkEnd();
    return events;
  }

  // ---- invasion machinery ---------------------------------------------------
  _frontiers(f) { return this.list().filter((r) => r.owner === 'ashen' && r.neighbors.some((n) => this.regions[n].owner === f)); }
  _onlySeat(f) { const owned = this.list().filter((r) => r.owner === f); return owned.length === 1 && owned[0].seat; }

  _launchInvasion(f, strength) {
    const fronts = this._frontiers(f);
    // prefer an undefended frontier (weakest garrison); else reinforce an existing front of ours
    const fresh = fronts.filter((r) => !r.threat).sort((a, b) => a.garrison - b.garrison);
    if (fresh.length) {
      const target = fresh[0];
      target.threat = { faction: f, strength, days: CFG.THREAT_DAYS };
      return `▲ ${FACTIONS[f].name} march on ${target.name}! ${CFG.THREAT_DAYS} days to answer.`;
    }
    const own = fronts.filter((r) => r.threat && r.threat.faction === f);
    if (own.length) { const t = this._pick(own).threat; t.strength += Math.round(strength * 0.4); t.days = Math.min(t.days, CFG.THREAT_DAYS); return null; }
    return null;
  }

  // ---- dispatch -------------------------------------------------------------
  dispatch(regionId, generalName) {
    const g = this.generals[generalName]; const r = this.regions[regionId];
    if (!g || g.status !== 'ready' || !r) return false;
    g.status = 'marching'; g.dest = regionId; g.mode = r.owner === 'ashen' ? 'defend' : 'assault'; g.eta = CFG.DISPATCH_TRAVEL;
    this._log(`${g.name} raises an army and marches on ${r.name} (${CFG.DISPATCH_TRAVEL} days).`);
    return true;
  }

  resolveDispatch(g) {
    const r = this.regions[g.dest];
    if (!r) return `${g.name} finds nothing to fight.`;
    if (g.mode === 'defend') {
      const t = r.threat;
      if (!t) return `${g.name} reaches ${r.name}; the threat had already passed.`;
      const force = g.power + Math.round(r.garrison * 0.5) + this._rnd(-15, 25);
      if (force >= t.strength) { r.threat = null; r.garrison = Math.min(r.baseGarrison, r.garrison + 20); return `✔ ${g.name} broke ${FACTIONS[t.faction].name} at ${r.name}.`; }
      this._capture(r, t.faction);
      return `✖ ${g.name} was overrun at ${r.name} — it falls to ${FACTIONS[t.faction].name}.`;
    }
    const def = r.garrison + this._rnd(-10, 20);
    if (g.power >= def) { this._capture(r, 'ashen'); return `✔ ${g.name} stormed ${r.name} — it flies the Ashen banner!`; }
    r.garrison = Math.max(40, Math.round(r.garrison - g.power * 0.3));
    return `✖ ${g.name}'s assault on ${r.name} was thrown back.`;
  }

  // ---- outcome of a battle the PLAYER fought --------------------------------
  applyResult(regionId, mode, win) {
    const r = this.regions[regionId];
    if (!r) return;
    if (mode === 'defend') {
      if (win) { const t = r.threat; r.threat = null; r.garrison = Math.min(r.baseGarrison, r.garrison + 30); this._log(`✔ Benjen held ${r.name}${t ? ' against ' + FACTIONS[t.faction].name : ''}.`); }
      else { const t = r.threat; if (t) this._capture(r, t.faction); this._log(`✖ ${r.name} is lost.`); }
    } else if (mode === 'assault') {
      if (win) { this._capture(r, 'ashen'); this._log(`✔ Benjen took ${r.name} for the Ashen Host!`); }
      else { r.garrison = Math.max(40, Math.round(r.garrison * 0.7)); this._log(`✖ The assault on ${r.name} failed.`); }
    }
    this._checkEnd();
  }

  _capture(r, faction) {
    const prev = r.owner;
    r.owner = faction; r.threat = null;
    r.garrison = faction === 'ashen' ? Math.round(r.baseGarrison * 0.7) : r.baseGarrison;
    if (faction === 'ashen' && ENEMY_FACTIONS.includes(prev)) { r.capturedFrom = prev; r.unrest = 0.42; if (this.factionState[prev]) this.factionState[prev].grudge += CFG.GRUDGE_HIT; }
    else r.capturedFrom = null, r.unrest = 0;
    if (r.seat && prev !== faction && ENEMY_FACTIONS.includes(prev) && faction === 'ashen') {
      this.factionDefeated[prev] = true;
      for (const o of this.list()) if (o.owner === prev) { o.owner = 'ashen'; o.threat = null; o.garrison = Math.round(o.baseGarrison * 0.6); o.capturedFrom = prev; o.unrest = 0.35; }
      this._log(`★ ${FACTIONS[prev].name} is broken! Their lands fly the Ashen banner.`);
    }
  }

  _checkEnd() {
    if (this.over) return;
    if (this.regions.redmarch.owner !== 'ashen') { this.over = 'lose'; this._log('☠ Redmarch is lost. The Ashen Host is broken. The war is over.'); return; }
    if (ENEMY_FACTIONS.every((f) => this.factionDefeated[f])) { this.over = 'win'; this._log('👑 The realm is united under the Ashen banner. You have won the war!'); }
  }

  // ---- event effect helpers (used by the deck) ------------------------------
  _aliveEnemies() { return ENEMY_FACTIONS.filter((f) => !this.factionDefeated[f]); }
  _regionsBiome(biome, owner) { return this.list().filter((r) => r.biome === biome && (owner === undefined || (owner === 'enemy' ? r.owner !== 'ashen' : r.owner === owner))); }
  _cutHeat(f, amt) { if (this.factionState[f]) this.factionState[f].heat = Math.max(0, this.factionState[f].heat - amt); }
  _drain(r, amt) { r.garrison = Math.max(30, Math.round(r.garrison - amt)); }
  _surge(r, amt) { r.garrison = Math.round(r.garrison + amt); }
  _addMerc() {
    const nm = MERC_NAMES[this._mercN % MERC_NAMES.length]; this._mercN++;
    const name = 'Free Company of ' + nm.replace(/^the /, '');
    this.generals[name] = { name, power: this._int(120, 175), status: 'ready', cd: 0, dest: null, mode: null, eta: 0, weapon: 'greatsword', merc: true, leaveDay: this.day + this._int(CFG.MERC_DAYS[0], CFG.MERC_DAYS[1]) };
    return name;
  }

  // ---- draw a weighted, eligible event --------------------------------------
  _drawEvent() {
    const pool = EVENTS.filter((e) => e.cond(this));
    if (!pool.length) return null;
    let total = 0; for (const e of pool) total += e.weight;
    let roll = this.rng() * total;
    for (const e of pool) { roll -= e.weight; if (roll <= 0) return e; }
    return pool[pool.length - 1];
  }

  // ---- battle-def bridge ----------------------------------------------------
  battleDef(regionId, mode) {
    const r = this.regions[regionId];
    const bordering = () => { for (const n of r.neighbors) { const o = this.regions[n]; if (o.owner !== 'ashen') return o.owner; } return 'magmar'; };
    const facKey = mode === 'defend' ? (r.threat ? r.threat.faction : bordering()) : r.owner;
    const strength = mode === 'defend' ? (r.threat ? r.threat.strength : 120) : r.garrison;
    const tier = Math.min(1.6, 0.85 + strength / 300);
    const dens = Math.round(200 + strength * 0.6);
    const cmds = COMMANDERS[facKey] || COMMANDERS.magmar;
    const nWar = mode === 'assault' ? 2 : (strength > 130 ? 2 : 1);
    const warlords = this._shuffle(cmds.slice()).slice(0, Math.min(nWar, cmds.length));
    const allies = this._realReady().slice(0, 3).map((g) => g.name);   // mercs auto-resolve only; real generals fight in person
    const fac = FACTIONS[facKey];
    return {
      id: 'cq_' + regionId + '_' + mode,
      act: mode === 'defend' ? 'DEFEND YOUR LANDS' : 'WAR OF CONQUEST',
      name: (mode === 'defend' ? 'Defend ' : 'Assault ') + r.name,
      subtitle: `${fac.name} · ${r.desc}`,
      biome: r.biome, enemyFaction: facKey,
      mods: { enemyHp: tier, enemyDmg: Math.min(1.5, tier * 0.95), spawn: Math.min(1.5, tier), magmarNear: dens, allyNear: 130 },
      bastionsMagmar: mode === 'assault' ? [0, 1, 2, 3] : [],
      warlords, allyGenerals: allies.length ? allies : ['Haldric Vane'],
      warmaster: allies.includes('Haldric Vane') ? 'Haldric Vane' : null,
      morale: mode === 'defend' ? 52 : 48,
      objectives: mode === 'assault' ? [{ type: 'slayWarlords', count: warlords.length }, { type: 'capture', count: 2 }]
        : [{ type: 'slayWarlord', name: warlords[0] }, { type: 'rout', count: Math.round(90 + strength * 0.5) }],
      fail: [], intro: [],
    };
  }

  walkDef(regionId) {
    const r = this.regions[regionId];
    return {
      id: 'walk_' + regionId, act: 'YOUR LANDS', name: 'Ride through ' + r.name,
      subtitle: r.desc, biome: r.biome, enemyFaction: null, peaceful: true,
      mods: { enemyHp: 1, enemyDmg: 1, spawn: 0.7, magmarNear: 0, allyNear: 90 },
      bastionsMagmar: [], warlords: [], allyGenerals: this._realReady().slice(0, 3).map((g) => g.name),
      warmaster: null, morale: 60, objectives: [], fail: [], intro: [],
    };
  }

  // ---- persistence ----------------------------------------------------------
  serialize() {
    const regions = {};
    for (const r of this.list()) regions[r.id] = { owner: r.owner, garrison: r.garrison, threat: r.threat, unrest: r.unrest, capturedFrom: r.capturedFrom };
    const gens = {};
    for (const g of Object.values(this.generals)) gens[g.name] = { power: g.power, status: g.status, cd: g.cd, dest: g.dest, mode: g.mode, eta: g.eta, weapon: g.weapon, merc: g.merc, leaveDay: g.leaveDay };
    return { day: this.day, over: this.over, regions, gens, factionDefeated: this.factionDefeated, factionState: this.factionState, eventCd: this.eventCd, mercN: this._mercN, log: this.log.slice(-24) };
  }
  _load(s) {
    this.day = s.day || 1; this.over = s.over || null;
    this.log = Array.isArray(s.log) ? s.log.slice(-24) : [];
    this.eventCd = s.eventCd != null ? s.eventCd : this.eventCd; this._mercN = s.mercN || 0;
    if (s.factionDefeated) this.factionDefeated = { ...this.factionDefeated, ...s.factionDefeated };
    if (s.factionState) for (const f in this.factionState) if (s.factionState[f]) Object.assign(this.factionState[f], s.factionState[f]);
    if (s.regions) for (const id in s.regions) if (this.regions[id]) Object.assign(this.regions[id], s.regions[id]);
    if (s.gens) for (const name in s.gens) {
      if (this.generals[name]) Object.assign(this.generals[name], s.gens[name]);
      else if (s.gens[name].merc) this.generals[name] = { name, ...s.gens[name] };   // restore a sellsword company
    }
  }

  _log(line) { this.log.push(line); if (this.log.length > 60) this.log.shift(); }
}

// ============================================================================
// THE EVENT DECK — betrayals, revolts, plagues, omens, boons, intrigue, feuds.
// Each: cond(g)→eligible, fire(g)→mutates state + returns log line(s). `g` is a
// Conquest instance (its rng/helpers do the work). Weights are relative.
// ============================================================================
const EVENTS = [
  // ---------- OMENS ----------
  { id: 'comet', weight: 5, cond: () => true, fire: (g) => {
    const boon = g._chance(0.6);
    if (boon) { g.playerRegions().forEach((r) => g._surge(r, g._int(6, 12))); return '☄ A comet burns over the realm — the host takes it for an omen, and hearts are lifted.'; }
    g._aliveEnemies().forEach((f) => g.factionState[f].heat += 0.35);
    return '☄ A blood-red comet hangs in the sky — the enemy read it as a sign to march.';
  } },
  { id: 'prophecy', weight: 3, cond: () => true, fire: (g) => {
    const r = g._pick(g.playerRegions()); if (r) g._surge(r, g._int(8, 16));
    return `A wandering seer proclaims Benjen the realm's dawn — men flock to the banner at ${r ? r.name : 'the camp'}.`;
  } },

  // ---------- BOONS ----------
  { id: 'harvest', weight: 5, cond: (g) => g.regions.goldfen.owner === 'ashen', fire: (g) => {
    const gv = g.regions.goldfen; g._surge(gv, g._int(20, 34));
    gv.neighbors.forEach((n) => { const o = g.regions[n]; if (o.owner === 'ashen') g._surge(o, g._int(6, 12)); });
    return '🌾 A bountiful harvest in Goldfen Vale swells the host — granaries and muster-rolls both fill.';
  } },
  { id: 'sellswords', weight: 4, cond: (g) => Object.keys(g.generals).filter((n) => g.generals[n].merc).length < 2, fire: (g) => {
    const nm = g._addMerc(); return `⚑ ${nm} take your coin — a sellsword company rides under the Ashen banner for a season.`;
  } },
  { id: 'envoy', weight: 4, cond: (g) => g.playerRegions().length > 0, fire: (g) => {
    const r = g._pick(g.list().filter((x) => g.isFrontline(x.id))) || g._pick(g.playerRegions());
    g._surge(r, g._int(14, 24));
    return `An envoy from a free hold pledges spears to ${r.name} — the border stiffens.`;
  } },
  { id: 'spy', weight: 4, cond: (g) => g._aliveEnemies().some((f) => g._frontiers(f).length), fire: (g) => {
    const f = g._aliveEnemies().sort((a, b) => g.factionState[b].heat - g.factionState[a].heat)[0];
    const fr = g._frontiers(f); const t = fr.sort((a, b) => a.garrison - b.garrison)[0];
    // buy the threatened land a little more warning
    g.list().forEach((r) => { if (r.threat && r.threat.faction === f) r.threat.days += 1; });
    return `🕵 A spy returns from the ${FACTIONS[f].name}: they mean to fall on ${t ? t.name : 'the marches'} next. The watch is doubled.`;
  } },
  { id: 'warlord_defects', weight: 2, cond: (g) => g._aliveEnemies().some((f) => g._frontiers(f).length), fire: (g) => {
    const f = g._pick(g._aliveEnemies().filter((x) => g._frontiers(x).length));
    g._cutHeat(f, 1.0);
    const enemyBorder = g.list().find((r) => r.owner === f && r.neighbors.some((n) => g.regions[n].owner === 'ashen'));
    if (enemyBorder) g._drain(enemyBorder, g._int(20, 40));
    const nm = g._pick(COMMANDERS[f] || []);
    return `${nm}, a warlord of ${FACTIONS[f].name}, turns his cloak and rides to Benjen — his host wavers.`;
  } },

  // ---------- FACTION DYNAMICS ----------
  { id: 'enemy_feud', weight: 3, cond: (g) => {
    const alive = g._aliveEnemies();
    return alive.length >= 2 && g.list().some((r) => alive.includes(r.owner) && r.neighbors.some((n) => alive.includes(g.regions[n].owner) && g.regions[n].owner !== r.owner));
  }, fire: (g) => {
    const alive = g._aliveEnemies();
    const border = g.list().find((r) => alive.includes(r.owner) && r.neighbors.some((n) => alive.includes(g.regions[n].owner) && g.regions[n].owner !== r.owner));
    const other = g.regions[border.neighbors.find((n) => alive.includes(g.regions[n].owner) && g.regions[n].owner !== border.owner)];
    g._drain(border, g._int(18, 34)); g._drain(other, g._int(12, 24));
    g._cutHeat(border.owner, 0.7); g._cutHeat(other.owner, 0.7);
    return `⚔ ${FACTIONS[border.owner].name} and ${FACTIONS[other.owner].name} fall to feuding along the ${border.name} march — both bleed, and their eyes turn from you.`;
  } },
  { id: 'enemy_thralls', weight: 3, cond: (g) => g.assaultable().length > 0, fire: (g) => {
    const r = g._pick(g.assaultable()); g._drain(r, g._int(24, 46));
    return `The thralls of ${r.name} throw off their masters — its garrison is gutted and ripe for the taking.`;
  } },
  { id: 'enemy_desertion', weight: 3, cond: (g) => g._aliveEnemies().length > 0, fire: (g) => {
    const f = g._pick(g._aliveEnemies()); g._cutHeat(f, 0.9);
    return `Word comes that ${FACTIONS[f].name} sag with desertion and hunger — their next push falters before it begins.`;
  } },

  // ---------- PLAGUES / WEATHER ----------
  { id: 'plague', weight: 4, cond: (g) => g.list().some((r) => r.biome === 'duskmarsh'), fire: (g) => {
    let hit = 0; g.list().forEach((r) => { if (r.biome === 'duskmarsh') { g._drain(r, g._int(16, 30)); hit++; } });
    // the Carrion breed in it — their pressure rises
    if (!g.factionDefeated.carrion) g.factionState.carrion.heat += 0.3;
    return `☣ Plague creeps out of the fens — garrisons sicken across the marshlands${hit ? '' : ''}. The Carrion thrive in the rot.`;
  } },
  { id: 'hard_winter', weight: 4, cond: (g) => !g.factionDefeated.frost || g.list().some((r) => r.biome === 'frostmark' || r.biome === 'ironpeak'), fire: (g) => {
    const boon = g._chance(0.5) && !g.factionDefeated.frost;
    if (boon) { g._cutHeat('frost', 1.1); return '❄ A brutal winter locks the northern passes — the Frost Raiders bog down in their own snows.'; }
    const cold = g.list().filter((r) => r.owner === 'ashen' && (r.biome === 'frostmark' || r.biome === 'ironpeak'));
    const r = g._pick(cold.length ? cold : g.playerRegions()); if (r) g._drain(r, g._int(16, 28));
    return `❄ A killing frost grips ${r ? r.name : 'the north'} — the garrison huddles and thins.`;
  } },
  { id: 'eruption', weight: 3, cond: (g) => g.list().some((r) => (r.biome === 'ashlands' || r.biome === 'emberwood')), fire: (g) => {
    const near = g.list().filter((r) => r.owner === 'ashen' && (r.biome === 'ashlands' || r.biome === 'emberwood'));
    const r = g._pick(near.length ? near : g.playerRegions()); if (r) g._drain(r, g._int(14, 26));
    return `🌋 The slag-vents erupt and ashfall smothers ${r ? r.name : 'the south'} — crops and men choke alike.`;
  } },
  { id: 'famine', weight: 3, cond: (g) => g.playerRegions().length > 1, fire: (g) => {
    const r = g._pick(g.playerRegions()); g._drain(r, g._int(14, 26));
    return `Rot takes the stores of ${r.name} — hunger hollows the ranks.`;
  } },

  // ---------- REVOLTS ----------
  { id: 'loyalist_uprising', weight: 6, cond: (g) => g.list().some((r) => r.owner === 'ashen' && r.capturedFrom && r.unrest > 0.5), fire: (g) => {
    const r = g._pick(g.list().filter((x) => x.owner === 'ashen' && x.capturedFrom && x.unrest > 0.5));
    const f = r.capturedFrom;
    if (!g.factionDefeated[f] && r.neighbors.some((n) => g.regions[n].owner === f)) {
      // the old masters return: a fierce revolt-threat, short fuse
      r.threat = { faction: f, strength: Math.round(r.garrison * 0.9 + g._int(20, 50)), days: 3 };
      r.unrest = 0.2;
      return `☭ ${r.name} rises in revolt — ${FACTIONS[f].name} loyalists take the walls! 3 days to crush it.`;
    }
    // faction is broken/gone: a leaderless uprising just bloodies the garrison
    g._drain(r, g._int(24, 44)); r.unrest = Math.max(0, r.unrest - 0.4);
    return `☭ ${r.name} rises in a leaderless revolt — the garrison is mauled putting it down.`;
  } },
  { id: 'brigands', weight: 3, cond: (g) => g.playerRegions().length > 0, fire: (g) => {
    const r = g._pick(g.playerRegions()); g._drain(r, g._int(10, 20));
    return `Brigands and deserters raid the roads of ${r.name} — patrols are stretched thin.`;
  } },

  // ---------- BETRAYAL / INTRIGUE ----------
  { id: 'general_defects', weight: 3, cond: (g) => g.day > 8 && Object.values(g.generals).filter((x) => !x.merc && x.status !== 'gone').length >= 4, fire: (g) => {
    // any real general can turn — ready, resting or marching — a general leaving is drama, not a ready-pool quirk
    const gen = g._pick(Object.values(g.generals).filter((x) => !x.merc && x.status !== 'gone'));
    gen.status = 'gone'; gen.cd = 0; gen.dest = null; gen.mode = null; gen.eta = 0;
    return `✖ ${gen.name} grows proud and bitter, and marches his men from the war. The host is the poorer.`;
  } },
  { id: 'assassination', weight: 2, cond: (g) => Object.values(g.generals).some((x) => x.status === 'ready' && !x.merc), fire: (g) => {
    const gen = g._pick(g._realReady());
    if (g._chance(0.5)) { gen.status = 'wounded'; gen.cd = g._int(4, 7); return `A knife in the dark finds ${gen.name} — he lives, but will not ride for days.`; }
    return `An assassin steals into camp for ${gen.name}, but the guards run him down. A grim warning.`;
  } },
  { id: 'merc_betrayal', weight: 3, cond: (g) => Object.values(g.generals).some((x) => x.merc), fire: (g) => {
    const merc = g._pick(Object.values(g.generals).filter((x) => x.merc));   // even mid-march they can turn coat
    const r = g._pick(g.playerRegions()); if (r) g._drain(r, g._int(10, 20));
    delete g.generals[merc.name];
    return `✖ ${merc.name} break their contract and ride off with the pay-chest — ${r ? r.name : 'the camp'} is left short-handed.`;
  } },
];
